/**
 * Every colour decision the overlay makes, in one place.
 *
 * These used to be inline literals inside the 200-line stylesheet template in
 * renderer.ts, which is how `primary_color` ended up marking five unrelated
 * things at once (CTA, odds, meter fill, combo tag, TV chip) and how the card
 * grew a THIRD level of white (`.why` at `opacity:.72`, sitting between `text`
 * and `muted`). The owner's report on 2026-08-03 was that the card "shimmers" —
 * «рябит из-за обилия цвета в тексте».
 *
 * THE RULE: two text levels, `text` and `muted`. Grouping is done with
 * containment, spacing and italic — never by inventing a brightness. The
 * "exactly two text levels" test in test/visual.test.ts enforces it mechanically.
 */
export interface VisualTokens {
  /** Panel / peek background. */
  surface: string;
  /** The pick card's own surface. */
  cardBg: string;
  cardLine: string;
  cardShadow: string;
  /** Generic hairline — chips, panel edges. */
  line: string;
  /** WHITE #1 — bright. */
  text: string;
  /** WHITE #2 — muted. There is no white #3. */
  muted: string;
  /** The express's leg slab, reused by the single card's pick block. */
  legBg: string;
  meterTrack: string;
  /** Brand. Green marks the CTA and the confidence meter, and nothing else. */
  meterFill: string;
  /** NOT the brand — the odds are information, not an action. */
  oddsColor: string;
  /** The motivation sentence. Must be `text` or `muted`, never a third level. */
  whyColor: string;
  ctaBg: string;
  skHi: string;
  skLo: string;

  /* ── The launcher chip ────────────────────────────────────────────────────
   * These were bare consts in renderer.ts (LAUNCHER_BG / LAUNCHER_FG), outside
   * this file entirely — which is precisely how the chip and the card drifted
   * into unrelated surfaces: the card pass turned the card opaque cool #171A2B
   * while the chip stayed a near-white #F2F3F7 nobody re-examined. They live
   * here now so the two are governed together.
   * Owner-chosen from the launcher lab, 2026-08-03 (fill=D · border=D · breath=C).
   */
  /** Chip surface. On dark it is the CARD's surface — one component, not two. */
  launcherBg: string;
  launcherFg: string;
  /** The edge. A gradient, not a hairline — the loudest thing on the chip, and
   * the only place the brand appears on it. */
  launcherEdge: string;
  /** The idle breath: the edge blooms and fades on a 9s cycle. Colour only —
   * nothing moves, which is why it reads as alive without reading as a spinner. */
  launcherGlow: string;
}

export function visualTokens(dark: boolean, brand: string): VisualTokens {
  const text = dark ? "#ECEDF2" : "#16182A";
  const muted = dark ? "#8B8DA3" : "#6A6C82";
  return {
    surface: dark ? "rgba(17,19,30,0.97)" : "rgba(255,255,255,0.98)",
    // Opaque, with a faint cool cast, deliberately. The old translucent
    // rgba(255,255,255,0.035) let Parikara's near-black board through, so the
    // card read as a hole punched in their page rather than a surface resting
    // on it. The cast (and the indigo hairline below) is what makes the card
    // legibly OURS rather than a piece of the operator's own chrome.
    cardBg: dark ? "#171A2B" : "rgba(0,0,0,0.022)",
    cardLine: dark ? "rgba(120,130,255,0.16)" : "rgba(0,0,0,0.10)",
    cardShadow: "0 8px 28px rgba(0,0,0,0.55)",
    line: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    text,
    muted,
    legBg: dark ? "rgba(255,255,255,0.028)" : "rgba(0,0,0,0.028)",
    meterTrack: dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)",
    meterFill: brand,
    // NOT the brand colour. Green marks two things and only two: the CTA (what
    // you tap) and the confidence meter (our judgement). The odds figure earns
    // its prominence from size and contrast.
    oddsColor: text,
    // ⭐ 2026-08-16: promoted from `muted` to `text` (WHITE #1). The owner could
    // not read the advice on his phone — «I can barely see it with cursive and
    // selected color». ⚠️ The colour PASSED the automated check (5.28:1 on the
    // dark card, above WCAG AA's 4.5) — AA is calibrated for normal-weight body
    // text and says nothing about 11.5px ITALIC, which is what actually hurt.
    // Now 14.73:1 dark / 16.38:1 light, and renderer.ts drops the italic.
    //
    // ⛔ This does NOT break the two-white rule and must not be "fixed" back:
    // the rule forbids inventing a THIRD brightness (the old `opacity:.72`), not
    // using WHITE #1. `{text, muted, whyColor, oddsColor}` is still a 2-set, and
    // the "exactly two text levels" test still enforces it. Separation now comes
    // from the border rule and spacing alone, which is what the rule intended.
    whyColor: text,
    ctaBg: brand,
    skHi: dark ? "rgba(255,255,255,0.11)" : "rgba(0,0,0,0.09)",
    skLo: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",

    // Dark: the card's own surface, so chip and card read as one component.
    // Light: unchanged from the pre-2026-08-03 chip — the lab was run on
    // Parikara's dark board and a light-theme operator has not been looked at.
    launcherBg: dark ? "#171A2B" : "#F2F3F7",
    launcherFg: dark ? text : "#11131E",
    // Indigo → brand → indigo. The brand appears ONCE on the chip, in the edge;
    // it is still absent from the odds, the chips and the label.
    launcherEdge: `linear-gradient(110deg, rgba(120,130,255,0.85), ${brand}99, rgba(120,130,255,0.85))`,
    launcherGlow: "rgba(120,130,255,0.42)",
  };
}
